import { parseDate, parseToDate } from "./utils"
import { getAuctionItems, getAuctionByPartId } from "@/actions/auctions"
import autoTable from "jspdf-autotable"
import slugify from 'slugify';

// For requests
export const REVALIDATE_AUCTION_TIME = 60000 * 5 // 5 minutes
export const REVALIDATE_BID_TIME = 5000 // 5 seconds
export const REVALIDATE_AUCTION_ITEM_TIME = 10000 // 10 seconds
export const REVALIDATE_USER_AUCTIONS_TIME = 10000 // 10 seconds
export const REVALIDATE_FILTERS_TIME = 30000 // 30 seconds
export const BID_TIME_REMAINING_COUNTDOWN = 60 * 2000 // 2 minutes in milliseconds
export const FETCH_AUCTION_FAVORITE_TIME = 5 * 60000 // 5 minutes

// Used to add extra time to the end time of an auction item to make sure the item is not ended before the user can place a bid
export const EXTRA_BID_TIME = 5000 // 5 seconds

// Filter settings, note: this is also set in the server for calculating first available page
export const AUCTION_ITEMS_PER_PAGE = 50

export const checkIfAuctionItemEnded = (itemEndTime: any, serverTime: any) => {
    let endTimeStr = parseDate(itemEndTime);

    if (isNaN(endTimeStr) || !serverTime) {
        return true;
    }

    let currentTime = parseToDate(serverTime).getTime();

    return endTimeStr + EXTRA_BID_TIME < currentTime;
};

export const handlePrintAuctionsListItem = async (partAuctionId: number) => {
    const { jsPDF } = await import("jspdf");
    // Helper to load image as base64
    async function getBase64FromUrl(url: string) {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    // Fetch all auction items
    const itemsResponse = await getAuctionItems(partAuctionId, { limit: 0 });
    const auctionItems = itemsResponse?.auctionItems || [];
    // Fetch auction info
    const auction = await getAuctionByPartId(partAuctionId);
    const auctionName = auction?.auctionTitle || auction?.partTitle || "Auktion";

    // Load PNG logo image as base64
    const logoUrl = "/images/tovekslogo.png";
    const logoBase64 = await getBase64FromUrl(logoUrl);

    const pdf = new jsPDF();
    // Add logo (width: 50, height: 13, y: 8 for some top padding)
    pdf.addImage(logoBase64, 'PNG', 10, 8, 50, 13);

    // Add address details to the far right
    pdf.setFontSize(10);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const addressLines = [
        'Address: Box 158, 311 51 Ätran',
        'Tel: 0346-487 70',
        'E-post: info@tovek.se'
    ];
    // Calculate the max width of the address block
    const maxLineWidth = Math.max(...addressLines.map(line => pdf.getTextWidth(line)));
    const addressX = pageWidth - maxLineWidth - 10; // 10pt margin from right
    addressLines.forEach((line, i) => {
        pdf.text(line, addressX, 14 + i * 6); // y: 14 aligns with logo
    });

    // Divider with padding
    const dividerY = 28; // a bit below the logo and address
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(10, dividerY + 5, pageWidth - 10, dividerY + 5);

    let y = dividerY + 8; // padding below divider
    pdf.setFontSize(18);
    pdf.text(auctionName, 10, y + 10);
    y += 15;

    // Prepare table data for autoTable
    const tableBody = auctionItems.map((item: any) => [
        String(item.itemSortNo),
        item.itemTitle || "",
        item.itemLocation || "",
        item.itemVatValue === 0 ? "0%" : `${item.itemVatValue}%`
    ]);

    autoTable(pdf, {
        head: [["Rop", "Beskrivning", "Lagerort", "Moms"]],
        body: tableBody,
        startY: y,
        styles: { fontSize: 11, cellPadding: 2 },
        headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
        columnStyles: {
            0: { cellWidth: 18 }, // Rop
            1: { cellWidth: 90 }, // Beskrivning
            2: { cellWidth: 45 }, // Lagerort
            3: { cellWidth: 30 }, // Moms
        },
        margin: { left: 10, right: 10 },
    });

    const name = slugify(auctionName);
    pdf.save(`${name}.pdf`);
};